import React, { useState, useEffect } from 'react';
import { loginUser } from '../services/authService.js';
import { useAuth } from '../contexts/AuthContext.js';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useNavigation } from '../contexts/NavigationContext.js';
import { useLanguage } from '../contexts/LanguageContext.js';

function LoginForm() {
    const { t } = useLanguage();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [emailVerificationError, setEmailVerificationError] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [pendingInvitation, setPendingInvitation] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { isMobile } = useNavigation();

    const { csrfToken, login } = useAuth();

    // Check for verification status in URL parameters
    useEffect(() => {

        const params = new URLSearchParams(location.search);
        const verification = params.get('verification');
        const redirect = params.get('redirect');

        // Check for pending invitation
        if (redirect === 'invitation') {
            const storedInvitation = localStorage.getItem('pendingInvitation');
            if (storedInvitation) {
                try {
                    const invitation = JSON.parse(storedInvitation);
                    setPendingInvitation(invitation);
                } catch (error) {
                }
            }
        }

        if (verification) {
            switch (verification) {
                case 'success':
                    setSuccessMessage(t('auth.login.verificationSuccess'));
                    break;
                case 'already':
                    setSuccessMessage(t('auth.login.verificationAlready'));
                    break;
                case 'invalid':
                    setErrorMessage(t('auth.login.verificationInvalid'));
                    break;
                case 'error':
                    setErrorMessage(t('auth.login.verificationError'));
                    break;
                default:
                    // No message for unknown status
                    break;
            }
        }
    }, [location, t]);

    // Handle post-login redirection, including any pending invitation
    const handleRedirectAfterLogin = () => {
        // Check if there's a pending invitation
        if (pendingInvitation) {
            const { communityId, token } = pendingInvitation;
            
            // Clear the pending invitation from localStorage
            localStorage.removeItem('pendingInvitation');
            
            // Redirect to invitation page with the stored parameters
            navigate(`/invitation?token=${token}&community_id=${communityId}`);
            return;
        }
        
        // If there was a return URL in the query string, use that
        const params = new URLSearchParams(location.search);
        const returnUrl = params.get('returnUrl');
        if (returnUrl) {
            navigate(returnUrl);
            return;
        }
        
        // Default: go to dashboard
        navigate('/dashboard');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setEmailVerificationError(false);
        setSuccessMessage('');
        
        try {
            const response = await loginUser({ email, password });

            setSuccessMessage(
                pendingInvitation
                    ? t('auth.login.loginSuccessInvitation')
                    : t('auth.login.loginSuccess')
            );
            
            login(response.user);
            
            setTimeout(() => {
                handleRedirectAfterLogin();
            }, 1000);
        } catch (error) {
            
            // Check if it's an email verification error
            if (error.response?.status === 401 &&
                error.response?.data?.email_verified === false) {
                setEmailVerificationError(true);
                setErrorMessage(t('auth.login.emailNotVerified'));
            } else {
                setErrorMessage(error.response?.data?.message || t('auth.login.loginFailed'));
            }
        }
    };

    const handleResendVerification = async () => {
        try {
            const response = await fetch('/api/resend-verification', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (response.ok) {
                setSuccessMessage(t('auth.login.verificationSent'));
                setErrorMessage('');
            } else {
                setErrorMessage(data.message || t('auth.login.verificationFailed'));
            }
        } catch (error) {
            setErrorMessage(t('auth.login.verificationFailed'));
        }
    };

    return (
        <div className="container mt-5">
            <div className="text-center mb-4">
                <img 
                    src="/MUSI.ico" 
                    alt="MUSI Logo" 
                    className="img-fluid" 
                    style={{ maxHeight: isMobile ? '90px' : '120px' }} 
                />
                <h2 className="mt-3">MUSI</h2>
            </div>
            <div className="row justify-content-center">
                <div className="col-md-6">
                    {pendingInvitation && (
                        <div className="alert alert-info mb-3">
                            <strong>{t('auth.login.pendingInvitation')}</strong>
                            <p>{t('auth.login.pendingInvitationText')}</p>
                        </div>
                    )}
                    
                    {successMessage && (
                        <div className="alert alert-success mb-3" role="alert">
                            {successMessage}
                        </div>
                    )}
                    
                    {errorMessage && (
                        <div className="alert alert-danger mb-3">
                            {errorMessage}
                            {emailVerificationError && (
                                <div className="mt-2">
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={handleResendVerification}>
                                        {t('auth.login.resendVerification')}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <form onSubmit={handleSubmit} className="p-4 border rounded shadow-sm bg-white">
                        <div className="mb-3">
                            <label htmlFor="email" className="form-label">{t('auth.login.email')}</label>
                            <input
                                type="email"
                                id="email"
                                className="form-control"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="password" className="form-label">{t('auth.login.password')}</label>
                            <input
                                type="password"
                                id="password"
                                className="form-control"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>
                        <button type="submit" className="btn btn-primary w-100">{t('auth.login.loginButton')}</button>
                    </form>

                    <div className="text-end mb-3">
                        <Link to="/forgot-password" className="text-decoration-none">
                            {t('auth.login.forgotPassword')}
                        </Link>
                    </div>

                    <div className="mt-3 text-center">
                        <p>{t('auth.login.noAccount')} <Link to="/register">{t('auth.login.registerHere')}</Link></p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LoginForm;